<template>
  <div class="ui-page">
    <AppFilterToolbar>
      <template #primary><app-input v-model="search" design="new" type="search" placeholder="Search by client, file, flight number or issue" aria-label="Search issues" /></template>
      <app-select v-model="state" design="new" :options="stateOptions" :reduce="item => item.code" :clearable="false" placeholder="State" aria-label="Issue state" />
      <app-select v-model="category" design="new" :options="categoryOptions" :reduce="item => item.code" :clearable="false" placeholder="Issue type" aria-label="Issue type" />
      <div class="flight-issues-field"><app-input v-model="clientFilter" design="new" type="number" placeholder="Client ID" aria-label="Client ID" /></div>
      <div class="flight-issues-field"><app-input v-model="documentFilter" design="new" type="number" placeholder="Document ID" aria-label="Document ID" /></div>
      <template v-if="hasActiveFilters" #clear><AppClearFiltersButton design="new" icon-only @clear="clearFilters" /></template>
    </AppFilterToolbar>
    <AppContentSkeleton v-if="loading" label="Loading issues" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" icon-only @retry="fetchItems" />
    <app-no-results v-else-if="!items.length" design="new" :title="hasFilters ? 'No matching issues' : 'No issues'" text="" />
    <app-card v-else design="new" variant="table" header-density="compact">
      <AppDataTable design="new" density="dense" :columns="columns" :rows="items" clickable min-width="1200px" @row-click="openReview($event.row)">
        <template #cell-client="{ row }"><AppTableText :text="row.client.full_name" /></template>
        <template #cell-document="{ row }"><AppTableText :text="row.file && row.file.file_name" fallback="Not set" /></template>
        <template #cell-context="{ row }"><span class="ui-cell-inline"><app-status-badge design="new" :label="issueDirection(row)" :tone="directionTone(row)" compact /><AppTableText :text="issueSegment(row)" /></span></template>
        <template #cell-issue="{ row }"><span class="ui-cell-inline"><app-status-badge design="new" :label="metadataName('categories', row.category)" :color="metadataColor('categories', row.category)" compact /><app-status-badge design="new" :label="metadataName('states', row.state)" :color="metadataColor('states', row.state)" compact /><AppTableText :text="row.description" /></span></template>
        <template #cell-reported="{ row }"><span class="ui-cell-inline"><AppTableUser :name="row.reported_by ? userName(row.reported_by) : ''" /><AppTableText :text="formatDate(row.created_at)" /></span></template>
      </AppDataTable>
    </app-card>
    <AppPagination v-if="!loading && !error" design="new" placement="embedded" :page="pagination.page" :pages="pagination.pages" :count="pagination.count" :visible-count="items.length" :page-size="pagination.pageSize" @change="onPageChange" />
    <review-modal ref="reviewModal" @changed="fetchItems" />
  </div>
</template>
<script>
import { format, isValid, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'
import { getSupportFlightTicketIssues } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'
import AppTableUser from '@/components/AppTableUser.vue'
import { getFlightTicketIssueMetadata } from './metadata'
import ReviewModal from './ReviewModal'

export default {
  name: 'TechSupportFlightTicketsIssues',
  components: { AppContentSkeleton, AppErrorState, AppTableText, AppTableUser, ReviewModal },
  data() {
    return {
      items: [],
      columns: [
        { key: 'client', label: 'Client', width: '15%' },
        { key: 'document', label: 'Document', width: '18%' },
        { key: 'context', label: 'Direction / segment', width: '19%' },
        { key: 'issue', label: 'Issue', width: '30%' },
        { key: 'reported', label: 'Reported', width: '18%' },
      ],
      resetting: false,
      error: '',
      metadata: getFlightTicketIssueMetadata(),
      search: null,
      state: 'open',
      category: '',
      clientFilter: null,
      documentFilter: null,
      loading: false,
      pagination: {
        page: 1,
        pages: 1,
        count: 0,
        pageSize: 20,
      },
    }
  },
  computed: {
    stateOptions() {
      return [{ code: '', name: 'All states' }, ...this.metadata.states]
    },
    categoryOptions() {
      return [{ code: '', name: 'All issue types' }, ...this.metadata.categories]
    },
    hasFilters() {
      return Boolean(
        this.search
        || this.state
        || this.category
        || this.clientFilter
        || this.documentFilter
      )
    },
    hasActiveFilters() {
      return Boolean(this.search || this.state !== 'open' || this.category || this.clientFilter || this.documentFilter)
    },
  },
  watch: {
    state() {
      if (!this.resetting) this.resetPageAndFetch()
    },
    category() {
      if (!this.resetting) this.resetPageAndFetch()
    },
  },
  created() {
    this.debouncedFetch = debounce(() => { if (!this.resetting) this.resetPageAndFetch() }, 350)
    this.$watch('search', this.debouncedFetch)
    this.$watch('clientFilter', this.debouncedFetch)
    this.$watch('documentFilter', this.debouncedFetch)
    this.fetchItems()
  },
  beforeDestroy() {
    this.debouncedFetch.cancel()
  },
  methods: {
    clearFilters() {
      this.debouncedFetch.cancel()
      this.resetting = true
      this.search = null
      this.state = 'open'
      this.category = ''
      this.clientFilter = null
      this.documentFilter = null
      this.$nextTick(() => {
        this.debouncedFetch.cancel()
        this.resetting = false
        this.resetPageAndFetch()
      })
    },
    fetchItems() {
      this.error = ''
      this.loading = true
      return getSupportFlightTicketIssues({
        state: this.state || undefined,
        category: this.category || undefined,
        client_id: this.clientFilter || undefined,
        document_id: this.documentFilter || undefined,
        search: this.search || undefined,
        page: this.pagination.page,
        page_size: this.pagination.pageSize,
      }).then(response => {
        this.items = response.data.results
        this.pagination.count = response.data.count
        this.pagination.pages = response.data.pages
      }).catch(() => {
        this.error = 'Issues could not be loaded. Please try again.'
      }).finally(() => {
        this.loading = false
      })
    },
    resetPageAndFetch() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onPageChange(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    metadataItem(group, code) {
      return this.metadata[group].find(item => item.code === code) || null
    },
    metadataName(group, code) {
      return this.metadataItem(group, code)?.name || this.humanize(code)
    },
    metadataColor(group, code) {
      return this.metadataItem(group, code)?.color || null
    },
    issueDirection(issue) {
      const code = issue.direction
        || issue.expected_data?.direction
        || issue.observed_data?.direction
      if (!code) return 'Whole document'
      return this.metadataName('directions', code)
    },
    directionTone(issue) {
      const direction = issue.direction
        || issue.expected_data?.direction
        || issue.observed_data?.direction
      if (direction === 'arrival') return 'green'
      if (direction === 'departure') return 'blue'
      return 'neutral'
    },
    issueSegment(issue) {
      const segment = issue.segment
      if (segment?.flight_number) return segment.flight_number
      if (issue.segment_id) return `Segment #${issue.segment_id}`
      return 'No segment selected'
    },
    userName(user) {
      if (!user) return 'Not set'
      return user.full_name || [user.first_name, user.last_name].filter(Boolean).join(' ')
    },
    formatDate(value) {
      if (!value) return 'Not set'
      const date = parseISO(value)
      return isValid(date) ? format(date, 'dd MMM yyyy') : value
    },
    humanize(value) {
      if (!value) return 'Not set'
      const words = String(value).replace(/_/g, ' ')
      return `${words.charAt(0).toUpperCase()}${words.slice(1)}`
    },
    openReview(issue) {
      this.$refs.reviewModal.open(issue.client.id, { documentId: issue.document_id })
    },
  },
}
</script>
<style lang="scss" scoped>
.flight-issues-field { flex: 0 0 $ui-filter-control-width; width: $ui-filter-control-width; min-width: 0; max-width: 100%; }
</style>
